<template lang="pug">
  GmapMap.client-map(
    v-loading="loading"
    ref="mapRef"
    :center="center"
    :zoom="zoom"
    :tilt=0
    map-type-id="hybrid"
    :style="'width:400px;height:300px'"
  )
</template>

<script>
import sensorIcon from '@assets/map/soil-sensor.svg';
import sensorSuboptimalIcon from '@assets/map/soil-sensor-suboptimal.svg';
import { gmapApi } from "vue2-google-maps";
import { mapState } from "vuex";
import { getRomaniaCoordsIfLocationIsOff } from "@utils/map";
import {toRaw} from 'vue';
import i18n from "@i18n";
import {filterUtils} from "@utils/filterUtils";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

export default {
  name: "ClientGoogleMaps",
  mixins: [filterUtils],
  props: {
    dialogName: {
      type: String,
      required: false,
    },

    sensors: {
      type: Array,
      default: () => [],
    },

    fields: {
      type: Array,
      default: () => [],
    },

    soilParam: {
      type: String,
      required: false,
    },
  },

  computed: {
    ...mapState("map", ["coordinates", "sensing_radius", "changedDialog", "isGoogleLoaded"]),
    ...mapState("field", ["field", "selectedFieldInfoSettings"]),

    google: gmapApi,
  },

  data() {
    return {
      markers: [],
      circles: [],
      polygons: [],
      marker: null,
      circle: null,
      loading: false,
      
      style:'width:400px;height:300px',
      zoom: 5,
      center: {
        lat: 0,
        lng: 0,
      },

      userLocation: null,
    };
  },

  watch: {
    coordinates() {
      this.initMap()
    },

    sensors() {
      this.initMap()
    },

    soilParam(){
      this.initMap()
    }
  },

  created() {
    this.getUserLocation();
  },

  mounted() {
    if (this.google) {
      this.initMap();
    }
  },

  methods: {
    async initMap() {
      switch (this.dialogName) {
        case "Overview":
          this.handleMapFieldForOverviewMap();
          this.handleMapMarkerAndCicleForOverviewMap();
      }
    },

    async handleMapMarkerAndCicleForOverviewMap(){
      this.markers.map((marker) => toRaw(marker).setMap(null))
      this.circles.map((circle) => toRaw(circle).setMap(null))

      this.markers = []
      this.circles = []

      if (this.sensors.length === 0) {
        return;
      }

      this.center.lat = Number(this.sensors[0].latitude);
      this.center.lng = Number(this.sensors[0].longitude);
      this.zoom = 18;
      
      this.$refs.mapRef.$mapPromise.then((map) => {
        const tempMarkers = [];
        this.sensors.forEach((sensor) => {
          const contentString = `
            <div class="custom-info-window">
              <div style="text-align: center; font-weight: bold; font-size: 15px; color: #117a65; margin-bottom: 4px;">
                ${sensor.transmitter_name}
              </div>
              <div style="text-align: center; font-size: 12px; color: #666; margin-bottom: 8px;">
                No.${sensor.sensor_number} – ${sensor.name}
              </div>
              <div style="border-top: 1px solid #eee; margin: 8px 0;"></div>
              ${this.renderSensorInfo(sensor)}
            </div>
          `;

          const infoWindow = new this.google.maps.InfoWindow({
            content: contentString,
          });

          // infoWindow.addListener("closeclick", () => {
          //   map.setZoom(16);
          //   map.panTo({ lat: this.center.lat, lng: this.center.lng }); // or some saved overview center
          // });

          // marker

          const iconSize = 40; // Adjust size here
          let marker = null;

          const icon = this.iconForOptimalParams(sensor) == true ? sensorIcon : sensorSuboptimalIcon;

          marker = new this.google.maps.Marker({
            position: {
              lat: Number(sensor.latitude),
              lng: Number(sensor.longitude),
            },
            icon: {
              url: icon,
              scaledSize: new this.google.maps.Size(iconSize, iconSize),
              labelOrigin: new this.google.maps.Point(iconSize / 2, -6),
            },
            label: {
              text: this.handleTextByParam(sensor), // or sensor.sensor_number
              color: "#FFFFFF", // white is more readable on dark imagery
              fontSize: "14px",
              fontWeight: "bold",
              background: "rgba(0, 0, 0, 0.6)", // semi-transparent black background
              padding: "2px 4px",
              borderRadius: "4px",
              border: "1px solid #FFFFFF", // optional white border for contrast
              textShadow: "0px 0px 4px #ffffff", // makes text pop
            },
            draggable: false,
          });

          marker.addListener("click", () => {
            infoWindow.open({
              anchor: marker,
              // map,
              shouldFocus: false,
            });

            // map.panTo(marker.getPosition());

            // setTimeout(() => {
            //   map.setZoom(19);
            // }, 200);
          });

          this.markers.push(marker);
          tempMarkers.push(marker);

          //circle

          // let circle = null;

          // circle = new this.google.maps.Circle({
          //   // map: map,
          //   fillColor: "#FFFFFF",
          //   strokeColor: "#FFFFFF",
          //   strokeWeight: 1,
          //   fillOpacity: 0.6,
          //   strokeWidth: 5,
          //   center: {
          //     lat: Number(sensor.latitude),
          //     lng: Number(sensor.longitude),
          //   },
          //   radius: sensor.sensing_radius
          // });

          // this.circles.push(circle)
        });

        this.markers.map((marker) => toRaw(marker).setMap(map))
        // this.circles.map((circle) => toRaw(circle).setMap(map))
        // ✅ Cluster the markers
        if (this.clusterer) {
          this.clusterer.clearMarkers(); // clean up old clusters
        }
        this.clusterer = new MarkerClusterer({
          map,
          markers: tempMarkers,
          renderer: {
            render: ({ count, position, markers }) => {
              // Check if any marker has a suboptimal icon
              const hasSuboptimal = markers.some(m => {
                const raw = toRaw(m);
                return raw.icon?.url === sensorSuboptimalIcon;
              });

              // Define the icon color
              const fillColor = hasSuboptimal ? '#e74c3c' : '#8AC7DB'; // red if suboptimal, blue otherwise

              return new this.google.maps.Marker({
                position,
                label: {
                  text: String(count),
                  color: "black",
                  fontSize: "14px",
                  fontWeight: "bold",
                },
                icon: {
                  path: this.google.maps.SymbolPath.CIRCLE,
                  fillColor,
                  fillOpacity: 0.95,
                  strokeWeight: 1,
                  strokeColor: 'black',
                  scale: 22,
                },
              });
            },
          },
          });
        });
    },

    iconForOptimalParams(sensor) {
      if(sensor.last_report && this.soilParam){
        const s = sensor.last_report.soil_info;
        switch (this.soilParam) {
          case 'humidity':
            return s.humidity >= sensor.field_humidity_range1s && s.humidity <= sensor.field_humidity_range1e;
          case 'temperature':
            return s.temperature >= sensor.field_temperature_range1s && s.temperature <= sensor.field_temperature_range1e;
          case 'conductivity':
            return s.conductivity >= sensor.field_conductivity_range1s && s.conductivity <= sensor.field_conductivity_range1e;
          case 'ph':
            return s.ph >= sensor.field_ph_range1s && s.ph <= sensor.field_ph_range1e;
          case 'nitrogen':
            return s.nitrogen >= sensor.field_nitrogen_range1s && s.nitrogen <= sensor.field_nitrogen_range1e;
          case 'phosphorus':
            return s.phosphorus >= sensor.field_phosphorus_range1s && s.phosphorus <= sensor.field_phosphorus_range1e;
          case 'potassium':
            return s.potassium >= sensor.field_potassium_range1s && s.potassium <= sensor.field_potassium_range1e;
          default:
            return false;
        
        }
      }
      return false;
    },

    iconForOptimalParams2(data, sensor) {
      if(sensor.last_report && data.infoType){
        const s = sensor.last_report.soil_info;
        switch (data.infoType) {
          case 'humidity':
            return s.humidity < sensor.field_humidity_range1s || s.humidity > sensor.field_humidity_range1e;
          case 'temperature':
            return s.temperature < sensor.field_temperature_range1s || s.temperature > sensor.field_temperature_range1e;
          case 'conductivity':
            return s.conductivity < sensor.field_conductivity_range1s || s.conductivity > sensor.field_conductivity_range1e;
          case 'ph':
            return s.ph < sensor.field_ph_range1s || s.ph > sensor.field_ph_range1e;
          case 'nitrogen':
            return s.nitrogen < sensor.field_nitrogen_range1s || s.nitrogen > sensor.field_nitrogen_range1e;
          case 'phosphorus':
            return s.phosphorus < sensor.field_phosphorus_range1s || s.phosphorus > sensor.field_phosphorus_range1e;
          case 'potassium':
            return s.potassium < sensor.field_potassium_range1s || s.potassium > sensor.field_potassium_range1e;
          default:
            return false;
        }
      }
      return false;
    },

    async handleMapFieldForOverviewMap(){
      var polygoneCoords = [];

      this.polygons.map((polygon) => polygon.setMap(null))

      this.$refs.mapRef.$mapPromise.then((map) => {
        this.fields.forEach((field) => {
          polygoneCoords = [];
          field.field_coordinates.forEach( (field_coordinate) => {
            polygoneCoords.push({
              lat: Number(field_coordinate.latitude.toString()),
              lng: Number(field_coordinate.longitude.toString())
            })
          })

          const color = field.color ? field.color : '0000FF';
          var myPolygon = new google.maps.Polygon({
            paths: polygoneCoords,
            strokeColor: `#${color}`,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: `#${color}`,
            fillOpacity: 0.35,
            // label: "yyyyy"
          });

          myPolygon.setMap(map);

          this.polygons.push(myPolygon)
        })

      });
    },

    renderSensorInfo(sensor) {
      if (!sensor.last_report || !sensor.last_report.soil_info) return '';

      const s = sensor.last_report.soil_info;
      // const shouldBold = this.iconForOptimalParams(sensor) === true;

      const rows = [
        { label: i18n.t("sensor_infos.created_at"), value: this.computeSensorDateFormat(sensor.last_report.created_at), infoType: "created_at" },
        { label: i18n.t("sensor_infos.humidity"), value: s.humidity ? `${this.handleTextByParam2("humidity", sensor)}` : null, infoType: "humidity" },
        { label: i18n.t("sensor_infos.temperature"), value: s.temperature ? `${this.handleTextByParam2("temperature", sensor)}` : null, infoType: "temperature" },
        { label: i18n.t("sensor_infos.conductivity"), value: s.conductivity ? `${this.handleTextByParam2("conductivity", sensor)}` : null, infoType: "conductivity" },
        { label: i18n.t("sensor_infos.ph"), value: s.ph ? `${this.handleTextByParam2("ph", sensor)}` : null, infoType: "ph" },
        { label: i18n.t("sensor_infos.nitrogen"), value: s.nitrogen ? `${this.handleTextByParam2("nitrogen", sensor)}` : null, infoType: "nitrogen" },
        { label: i18n.t("sensor_infos.phosphorus"), value: s.phosphorus ? `${this.handleTextByParam2("phosphorus", sensor)}` : null, infoType: "phosphorus" },
        { label: i18n.t("sensor_infos.potassium"), value: s.potassium ? `${this.handleTextByParam2("potassium", sensor)}` : null, infoType: "potassium" },
      ];

      return `
        <div style="display: grid; grid-template-columns: auto auto; row-gap: 6px; column-gap: 8px;">
          ${rows
            .filter(r => r.value)
            .map(r => {
              const shouldBold = this.iconForOptimalParams2(r, sensor);
              const notification = sensor[`snooze_notification_${r.infoType}`];  
              const fontWeight = shouldBold ? 'bold' : '500';
              const shouldBeRed = shouldBold ? '#ff0000' : '#000000';

              let iconHtml = '';
              if (shouldBold) {
                if (notification) {
                  // snoozed
                  iconHtml = `
                    <span 
                      style="display: inline-flex; justify-content: center; align-items: center; width: 16px; height: 16px; border-radius: 3px; font-size: 12px; font-weight: bold; cursor: pointer;"
                      title="Snoozed until ${new Date(notification).toLocaleString()}"
                    >🔕</span>
                  `;
                } else {
                  // active alert
                  // iconHtml = `
                  //   <span 
                  //     style="display: inline-flex; justify-content: center; align-items: center; width: 16px; height: 16px; border-radius: 3px; font-size: 12px; font-weight: bold; cursor: pointer;"
                  //     title="Alert active"
                  //   >🔔</span>
                  // `;
                }
              }

              return `
                <div style="color: #555; font-weight: ${fontWeight}; display: flex; align-items: center; gap: 4px;">
                  <span>${r.label}:</span>
                  ${iconHtml}
                </div>
                <div style="text-align: right; color: ${shouldBeRed}; ">${r.value}</div>
              `;
            }).join('')}
        </div>
      `;
    },

    handleTextByParam(sensor) {
      if(sensor.last_report && this.soilParam){
        const s = sensor.last_report.soil_info;
        let lowOrHighValue = "";
        switch (this.soilParam) {
          case 'humidity':
           lowOrHighValue = "";
            if (s.humidity !== null && s.humidity < sensor.field_humidity_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.humidity !== null && s.humidity > sensor.field_humidity_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.humidity ? s.humidity.toString() : "-"} ${i18n.t("overview.map.um.humidity")}`;
          case 'temperature':
            lowOrHighValue = "";
            if (s.temperature !== null && s.temperature < sensor.field_temperature_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.temperature !== null && s.temperature > sensor.field_temperature_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.temperature ? s.temperature.toString() : "-"} ${i18n.t("overview.map.um.temperature")}`;
          case 'conductivity':
            if (s.conductivity !== null && s.conductivity < sensor.field_conductivity_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.conductivity !== null && s.conductivity > sensor.field_conductivity_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.conductivity ? s.conductivity.toString() : "-"} ${i18n.t("overview.map.um.conductivity")}`;
          case 'ph':
            if (s.ph !== null && s.ph < sensor.field_ph_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.ph !== null && s.ph > sensor.field_ph_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.ph ? s.ph.toString() : "-"}`;
          case 'nitrogen':
            if (s.nitrogen !== null && s.nitrogen < sensor.field_nitrogen_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.nitrogen !== null && s.nitrogen > sensor.field_nitrogen_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.nitrogen ? s.nitrogen.toString() : "-"} ${i18n.t("overview.map.um.nitrogen")}`;
          case 'phosphorus':
            if (s.phosphorus !== null && s.phosphorus < sensor.field_phosphorus_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.phosphorus !== null && s.phosphorus > sensor.field_phosphorus_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.phosphorus ? s.phosphorus.toString() : "-"} ${i18n.t("overview.map.um.phosphorus")}`;
          case 'potassium':
            if (s.potassium !== null && s.potassium < sensor.field_potassium_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.potassium !== null && s.potassium > sensor.field_potassium_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.potassium ? s.potassium.toString() : "-"} ${i18n.t("overview.map.um.potassium")}`;
          default:
            return 'not found';
        }
      }
    },

    handleTextByParam2(type, sensor) {
      if(sensor.last_report && type){
        const s = sensor.last_report.soil_info;
        let lowOrHighValue = "";
        switch (type) {
          case 'humidity':
            lowOrHighValue = "";
            if (s.humidity !== null && s.humidity < sensor.field_humidity_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.humidity !== null && s.humidity > sensor.field_humidity_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.humidity ? s.humidity.toString() : "-"} ${i18n.t("overview.map.um.humidity")}`;
          case 'temperature':
            lowOrHighValue = "";
            if (s.temperature !== null && s.temperature < sensor.field_temperature_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.temperature !== null && s.temperature > sensor.field_temperature_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.temperature ? s.temperature.toString() : "-"} ${i18n.t("overview.map.um.temperature")}`;
          case 'conductivity':
            if (s.conductivity !== null && s.conductivity < sensor.field_conductivity_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.conductivity !== null && s.conductivity > sensor.field_conductivity_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.conductivity ? s.conductivity.toString() : "-"} ${i18n.t("overview.map.um.conductivity")}`;
          case 'ph':
            if (s.ph !== null && s.ph < sensor.field_ph_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.ph !== null && s.ph > sensor.field_ph_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.ph ? s.ph.toString() : "-"}`;
          case 'nitrogen':
            if (s.nitrogen !== null && s.nitrogen < sensor.field_nitrogen_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.nitrogen !== null && s.nitrogen > sensor.field_nitrogen_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.nitrogen ? s.nitrogen.toString() : "-"} ${i18n.t("overview.map.um.nitrogen")}`;
          case 'phosphorus':
            if (s.phosphorus !== null && s.phosphorus < sensor.field_phosphorus_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.phosphorus !== null && s.phosphorus > sensor.field_phosphorus_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.phosphorus ? s.phosphorus.toString() : "-"} ${i18n.t("overview.map.um.phosphorus")}`;
          case 'potassium':
            if (s.potassium !== null && s.potassium < sensor.field_potassium_range1s) {
              lowOrHighValue = "↓ ";
            }
            if (s.potassium !== null && s.potassium > sensor.field_potassium_range1e) {
              lowOrHighValue = "↑ ";
            }
            return `${lowOrHighValue}${s.potassium ? s.potassium.toString() : "-"} ${i18n.t("overview.map.um.potassium")}`;
          default:
            return 'not found';
        }
      }
    },

    handleInfoColor(info){
      if(info >= this.soilParam.range1s && info < this.soilParam.range1e){
        return this.soilParam.range1color
      }else if(info >= this.soilParam.range2s && info < this.soilParam.range2e){
        return this.soilParam.range2color
      }else if(info >= this.soilParam.range3s && info < this.soilParam.range3e){
        return this.soilParam.range3color
      }
    },

    renderSensorType(sensor){
      if(sensor.sensor_type)
        switch (sensor.sensor_type) {
          case '0e':
            return 'NPK';
          case 'ee':
            return 'THCNPK';
          case 'de':
            return 'THPHNPK';
          case '10':
            return 'PH';
          case 'c0':
            return 'TH';
          case 'e0':
            return 'THC';
          case 'f0':
            return 'THCPH';
          case 'd0':
            return 'THPH';
          case 'fe':
            return 'THCPHNPK';
          default:
            return 'THC';
        }
    },

    async getUserLocation() {
      navigator.geolocation.getCurrentPosition(currentBrowserLocation => {
        this.userLocation = currentBrowserLocation;
      })

      // if location is not working or turned off
      const romaniaCoords = await getRomaniaCoordsIfLocationIsOff();
      if (romaniaCoords) {
        this.userLocation = romaniaCoords;
        if (!this.coordinates.lat) {
          this.zoom = 7;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.gm-style-iw:has(.custom-info-window) .gm-ui-hover-effect) {
  top: 25px !important;
  right: 4px !important;
  width: 20px !important;
  height: 20px !important;
  border-radius: 50%;
  display: flex !important;
  align-items: center;
  justify-content: center;
}

::v-deep(.gm-style-iw:has(.custom-info-window) .gm-ui-hover-effect img) {
  display: none !important;
}

::v-deep(.gm-style-iw:has(.custom-info-window) .gm-ui-hover-effect) {
  position: relative;
}

::v-deep(.gm-style-iw:has(.custom-info-window) .gm-ui-hover-effect::before),
::v-deep(.gm-style-iw:has(.custom-info-window) .gm-ui-hover-effect::after) {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 2px;
  background: #333; /* color of the X */
  transform-origin: center;
}

::v-deep(.gm-style-iw:has(.custom-info-window) .gm-ui-hover-effect::before) {
  transform: translate(-50%, -50%) rotate(45deg);
}

::v-deep(.gm-style-iw:has(.custom-info-window) .gm-ui-hover-effect::after) {
  transform: translate(-50%, -50%) rotate(-45deg);
}
</style>

